import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState } from 'react';

import { PriceOptionDto } from '@cineamo/api/models/price-option/PriceOptionDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconTick16 } from '@cineamo/ui/src/icons/ic-tick';
import { Seat as BaseSeat } from '@cineamo/ui/src/seat-map/typedefs';

import { SelectedSeat, useCartProvider } from '@cineamo/composables/src/providers/cartProvider';
import CartItem from '@cineamo/composables/src/ticket-booking/CartItem';

export interface Seat extends BaseSeat {
    priceOptionId?: PriceOptionDto['id'];
}

const SelectedSeatCartItem: React.FC<{
    className?: string;
    seat: Seat;
    selectedSeat: SelectedSeat;
    isDisabled: boolean;
    onRemove?: () => void;
}> = ({ className, seat, selectedSeat, isDisabled, onRemove }) => {
    const t = useTranslations('tickets');
    const { applyPriceOption, currency, priceOptions } = useCartProvider();

    const [isProcessing, setIsProcessing] = useState(false);
    const formatter = useFormat();

    const priceOptionsForSeat = useMemo(
        () =>
            priceOptions
                .filter((o) => o._embedded?.seatCategory?.id === seat.category?.id)
                .map((priceOption) => ({
                    ...priceOption,
                    price: priceOption.priceTotal
                })),
        [priceOptions, seat.category?.id]
    );

    const defaultPriceOption = useMemo(
        () => priceOptionsForSeat.find((priceOption) => priceOption.isShowingDefault),
        [priceOptionsForSeat]
    );

    const selectedPriceOption = useMemo(() => {
        const priceOption = priceOptions.find((priceOption) => priceOption.id === selectedSeat.priceOptionId);
        return priceOption ?? defaultPriceOption;
    }, [selectedSeat.priceOptionId, defaultPriceOption, priceOptions]);

    const selectPriceOption = useCallback(
        (priceOption: PriceOptionDto) => {
            setIsProcessing(true);
            applyPriceOption(priceOption.id, selectedSeat.id).finally(() => {
                setIsProcessing(false);
            });
        },
        [applyPriceOption, selectedSeat.id]
    );

    return (
        <CartItem
            className={cn(
                'bg-[linear-gradient(180deg,var(--tw-gradient-stops))] from-[rgba(var(--on-background-color-rgb),0.05)] to-[rgba(var(--on-background-color-rgb),0.1)]',
                'p-[8px] rounded-4',
                className
            )}
            title={t('selected-seat.title', {
                row: seat.rowName ?? 'N/A',
                seat: seat.positionName ?? 'N/A'
            })}
            subtitle={
                selectedPriceOption?._embedded?.priceDiscount?.name
                    ? `1x ${selectedPriceOption?._embedded?.priceDiscount?.name}`
                    : undefined
            }
            buttonLabel={t('change-price-type')}
            price={selectedPriceOption?.priceTotal ?? undefined}
            currency={currency}
            isDisabled={isDisabled || priceOptionsForSeat.length <= 1}
            isProcessing={isProcessing}
            onRemove={onRemove}
            removeLabel={t('remove-seat')}>
            {/* Figma "Interaction List" (Cinema Websites 503:90243): a light card of "category … price" rows. */}
            {priceOptionsForSeat.length > 1 && (
                <ul
                    role="listbox"
                    aria-label={t('change-price-type')}
                    className="mt-8 flex flex-col divide-y-[0.5px] divide-gray overflow-clip rounded-8 bg-white text-darkBlue shadow-[0_12px_24px_0_rgba(0,0,35,0.1)]">
                    {priceOptionsForSeat.map((priceOption) => {
                        const isSelected = selectedPriceOption?.id === priceOption.id;
                        return (
                            <li key={seat.id + '-' + priceOption.id}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    className={cn(
                                        'flex w-full items-end justify-between gap-16 px-16 py-8 text-left outline-none',
                                        'focus-visible:bg-gray-light hover:bg-gray-light',
                                        isSelected ? 'text-body-bold-14' : 'text-body-14'
                                    )}
                                    onClick={() => selectPriceOption(priceOption)}>
                                    <span className="flex items-center gap-8">
                                        {isSelected && <IconTick16 />}
                                        {priceOption._embedded?.priceDiscount?.name}
                                    </span>
                                    <span>
                                        {formatter.number.formatCurrency((priceOption.priceTotal ?? 0) / 100, currency)}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </CartItem>
    );
};

export default SelectedSeatCartItem;
